/**
 * The Customer Bot Dashboard.
 *
 * Its own application, on its own host, because it is run by different people: NAB's knowledge
 * owners maintain the internal corpus in the back office, and whoever owns the customer channel
 * owns what the chatbot says. See
 * `docs/adr/0014-the-customer-dashboard-is-its-own-application.md`.
 *
 * It shares the back office's `users` table and cookies, so an administrator has one account —
 * what changes is which application they open with it.
 *
 * ## Eight destinations, each a URL
 *
 * The design names eleven screens; several of them are read together and are tabs rather than
 * sidebar entries. Placeholders exist to be used in messages, so putting them two clicks apart
 * would mean three clicks to answer "why is this one red?".
 *
 * ## The navigation is role-driven; authorization is not
 *
 * Every endpoint under `/admin/v1` is role-gated on the server — `require_viewer` to read,
 * `require_editor` to change, and the approver check is on *identity* rather than role, because
 * the point of a second pair of eyes is that they belong to somebody else. Hiding a control in
 * the client is a presentation choice anyone can bypass with the developer tools. See
 * `customer_admin/api/rbac.py`.
 */

import { useTranslation } from "react-i18next";

import {
  AdminShell,
  AuthGate,
  SessionProvider,
  ToastProvider,
  useRoute,
  type NavEntry,
} from "@nab/shared";

import { BotNav } from "./components/BotNav";
import { BotProvider } from "./config";
import { ContentPage } from "./pages/ContentPage";
import { KnowledgePage } from "./pages/KnowledgePage";
import { DevelopersPage } from "./pages/DevelopersPage";
import { PlaygroundPage } from "./pages/PlaygroundPage";
import { SettingsPage } from "./pages/SettingsPage";
import { ToolsPage } from "./pages/ToolsPage";
import { UsersPage } from "./pages/UsersPage";
import { VersionsPage } from "./pages/VersionsPage";

type Page =
  | "tools"
  | "content"
  | "knowledge"
  | "playground"
  | "developers"
  | "versions"
  | "settings"
  | "users";

// Paths are stable: they appear in bookmarks and in links pasted into tickets, so renaming one
// breaks something outside this repository.
const NAVIGATION: readonly NavEntry<Page>[] = [
  { id: "tools", path: "/tools" },
  { id: "content", path: "/content" },
  // Between content and the playground: it is content the bot says, and the thing you most want
  // to try in the playground straight after changing.
  { id: "knowledge", path: "/knowledge" },
  { id: "playground", path: "/playground" },
  // The developer console. Offered to everyone who can sign in, and gated on the server like
  // everything else — a masked transcript of real conversations is still a transcript of real
  // conversations, so every route behind it requires a viewer.
  { id: "developers", path: "/developers" },
  { id: "versions", path: "/versions" },
  { id: "settings", path: "/settings" },
  // Accounts, last, beside settings: it is administration of the tool rather than of the bot.
  // Offered to everyone who can sign in, like every other entry, and refused on the server for
  // everyone who is not `admin` or `hr` — the page renders that refusal as a sentence, because
  // hiding the link makes "you do not have this role" look like "the dashboard is broken".
  { id: "users", path: "/users" },
];

export function App(): JSX.Element {
  const { t } = useTranslation("app");

  return (
    <SessionProvider>
      <ToastProvider regionLabel={t("toast.region")} closeLabel={t("toast.close")}>
        <AuthGate title={t("app.name")}>
          {/* The draft, loaded once and shared by all five screens. Every save replaces a row in
              the same draft, and five independent loads would disagree the moment somebody
              saved. */}
          <BotProvider>
            <Dashboard />
          </BotProvider>
        </AuthGate>
      </ToastProvider>
    </SessionProvider>
  );
}

function Dashboard(): JSX.Element {
  const { t } = useTranslation("app");
  const routes = NAVIGATION.map((entry) => ({ id: entry.id, path: entry.path! }));
  const page = useRoute(routes, "tools");

  return (
    <AdminShell
      title={t(`nav.${page}`)}
      nav={<BotNav routes={routes} activeId={page} />}
    >
      {page === "tools" ? <ToolsPage /> : null}
      {page === "content" ? <ContentPage /> : null}
      {page === "knowledge" ? <KnowledgePage /> : null}
      {page === "playground" ? <PlaygroundPage /> : null}
      {page === "developers" ? <DevelopersPage /> : null}
      {page === "versions" ? <VersionsPage /> : null}
      {page === "settings" ? <SettingsPage /> : null}
      {page === "users" ? <UsersPage /> : null}
    </AdminShell>
  );
}
